import React, { useMemo } from 'react';
import { RocketOutlined } from '@ant-design/icons';
import { Outlet, useNavigate } from 'react-router';
import styled from '@emotion/styled';
import Table from 'app/components/Table';
import { getColumnDefs } from './constants';
import { TablePaginationConfig, notification } from 'antd';
import { FilterValue } from 'antd/lib/table/interface';
import {
  useActiveTemplateMutation,
  useInActiveTemplateMutation,
  useSetDefaultTemplateMutation
} from 'app/features/templates/api';
import { TemplateInformation } from './views';
import { get, isEmpty, pick, set } from 'lodash';
import TemplateProvider, { TemplateContext } from './context';
import ListLayout from 'app/components/Layout/ListLayout';
import { ReactComponent as TemplateManagement } from 'assets/icons/navs/nav-1.svg';
import Icon from '@ant-design/icons';
import FilterBar, { EFilterItemType } from 'app/components/FilterBar';
import { ReactComponent as SortIcon } from 'assets/icons/toolbars/sort-icon.svg';
import moment from 'moment';
const Container = styled.div`
  min-height: 100%;
  text-align: left;
  background: none;
`;
const WrapperTable = styled.div`
  margin-top: 16px;
  border-radius: 4px;
  box-shadow: 0px 1px 6px 0px #a5b2c833;
  box-shadow: 0px 2px 4px 1px #a5b2c81a;
  background: #fff;
  padding: 24px;
  .ant-table-expanded-row {
    > td {
      background: rgba(250, 252, 255, 1);
    }
  }
`;

const StyledTotalLabel = styled.p`
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.005em;
  text-align: left;
  color: #8991a0;
`;

const StyledIcon = styled(Icon)`
  font-size: 24px;
  path {
    fill: #000;
  }
`;

const TemplateManagementPage = () => {
  return (
    <TemplateProvider>
      <Container>
        <Outlet></Outlet>
      </Container>
    </TemplateProvider>
  );
};
const Index = () => {
  const navigate = useNavigate();
  const { fetchList, list } = React.useContext(TemplateContext);

  const [active] = useActiveTemplateMutation();
  const [inActive] = useInActiveTemplateMutation();
  const [setDefaultTemplate] = useSetDefaultTemplateMutation();

  const templates = React.useMemo(() => {
    const { data } = list;
    return data;
  }, [list]);

  const onDetail = (id: number) => {
    navigate(`template/${id}`);
  };

  const onDuplicate = (id: number) => {
    navigate(`template?duplicate=${id}`);
  };

  const activeTemplate = async (id: string) => {
    try {
      const response: any = await active(id);
      if (response.error) {
        const message = get(response.error.data, 'msg.message');
        notification.error({ message, placement: 'bottomLeft' });
        return;
      }
      notification.success({
        message: 'Template activated!',
        placement: 'bottomLeft'
      });
      fetchList &&
        fetchList({
          ...(list.originalArgs as any)
        });
    } catch (error) {
      console.log('Active-Template-Error', error);
    }
  };

  const inActiveTemplate = async (id: string) => {
    try {
      const response: any = await inActive(id);
      if (response.error) {
        const message = get(response.error.data, 'msg.message');
        notification.error({ message, placement: 'bottomLeft' });
        return;
      }
      notification.success({
        message: 'Template inactivated!',
        placement: 'bottomLeft'
      });
      fetchList &&
        fetchList({
          ...(list.originalArgs as any)
        });
    } catch (error) {
      console.log('Active-Template-Error', error);
    }
  };

  const setDefault = async (id: string) => {
    try {
      await setDefaultTemplate(id);
      notification.success({
        message: 'Template set as default!',
        placement: 'bottomLeft'
      });
      fetchList &&
        fetchList({
          ...(list.originalArgs as any)
        });
    } catch (error) {
      console.error('Set-Default-Template-Error', error);
    }
  };

  const templateColumns = getColumnDefs(
    onDetail,
    onDuplicate,
    activeTemplate,
    inActiveTemplate,
    setDefault
  );

  const onAddTemplate = () => {
    navigate('template');
  };

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>
  ) => {
    const nextFilters = Object.keys(filters).reduce((acc: any, key) => {
      if (filters[key] === null) {
        return acc;
      }
      acc[key] = Array.isArray(filters[key]) ? filters[key]?.[0] : filters[key];
      return acc;
    }, {});

    fetchList &&
      fetchList({
        ...list.originalArgs,
        ...pagination,
        ...(nextFilters as any),
        excludes: ['scheme']
      });
  };

  const handleChangeFilter = (filter, sort) => {
    const previous = { ...list.originalArgs };
    const nextFilter = Object.keys(filter).reduce((acc: any, key) => {
      if (Array.isArray(filter[key])) {
        acc[key] = filter[key].filter((item) => item !== 'all');
      }
      return acc;
    }, {});

    const nextSort = Object.keys(sort).reduce((acc: any, key) => {
      const sorting = sort[key];
      if (!isEmpty(sorting)) {
        //Clear Previous Sort.
        if (previous) {
          set(previous, 'createdAtFrom', null);
          set(previous, 'createdAtTo', null);
          set(previous, 'updatedAtTo', null);
          set(previous, 'updatedAtFrom', null);

          delete previous['orderBy'];
          delete previous['createdAtFrom'];
          delete previous['createdAtTo'];
          delete previous['updatedAtTo'];
          delete previous['updatedAtFrom'];
        }

        const { sortBy, ranges, direction } = sorting;
        if (sortBy && direction) {
          acc['orderBy'] = `${sortBy}:${direction}`;
        }
        if (Array.isArray(ranges) && ranges.length > 1) {
          const [from, to] = ranges;
          if (from) {
            acc[sortBy === 'createdAt' ? 'createdAtFrom' : 'updatedAtFrom'] =
              from?.format('YYYY-MM-DD');
          }
          if (to) {
            acc[sortBy === 'createdAt' ? 'createdAtTo' : 'updatedAtTo'] =
              to?.format('YYYY-MM-DD');
          }
        }
      }

      return acc;
    }, {});

    fetchList({
      ...previous,
      ...nextFilter,
      ...nextSort,
      current: 1,
      pageSize: 10,
      excludes: 'scheme'
    });
  };

  const handleChangeKeyword = (keyword: string) => {
    const params: any = {
      current: 1,
      pageSize: 10,
      excludes: ['scheme']
    };
    if (keyword) {
      params.keywords = keyword;
    }
    fetchList(params);
  };

  const defaultSort = useMemo(() => {
    const defaultValue = {
      createdAt: {
        direction: 'asc',
        sortBy: 'createdAt',
        ranges: []
      }
    };
    const { orderBy, createdAtFrom, createdAtTo, updatedAtFrom, updatedAtTo } =
      list.originalArgs || {};

    if (orderBy && typeof orderBy === 'string') {
      const [key, value] = orderBy.split(':');
      set(defaultValue, `createdAt.direction`, value);
      set(defaultValue, `createdAt.sortBy`, key);
    }
    if (createdAtFrom && createdAtTo) {
      set(defaultValue, `createdAt.sortBy`, 'createdAt');
      set(defaultValue, `createdAt.ranges`, [
        moment(createdAtFrom),
        moment(createdAtTo)
      ]);
    }
    if (updatedAtFrom && updatedAtTo) {
      set(defaultValue, `createdAt.ranges`, [
        moment(updatedAtFrom),
        moment(updatedAtTo)
      ]);
    }

    return defaultValue;
  }, [list.originalArgs]);

  React.useEffect(() => {
    if (fetchList) {
      fetchList({
        ...list.originalArgs,
        current: 1,
        pageSize: 10,
        excludes: ['scheme']
      } as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ListLayout
      icon={
        <StyledIcon style={{ fontSize: 24 }} component={TemplateManagement} />
      }
      createButtonText="Create New Template"
      title="Template Management"
      keyword={list?.originalArgs?.keywords}
      searchInputProps={{
        placeholder: 'Search template no. , template Name',
        defaultValue: get(list, 'originalArgs.keywords', '')
      }}
      onCreate={onAddTemplate}
      onSearch={handleChangeKeyword}
    >
      <FilterBar
        value={{
          ...pick(list.originalArgs, ['status']),
          ...defaultSort
        }}
        defaultValue={{
          createdAt: {
            direction: 'asc',
            sortBy: 'createdAt',
            ranges: []
          },
          status: []
        }}
        onChange={handleChangeFilter}
        filterItems={[
          {
            key: 'createdAt',
            type: EFilterItemType.SORT_DATE,
            label: (_: any, sortBy: any) => {
              if (!sortBy) return 'Sort By Create Date';
              if (sortBy === 'createdAt') return `Sort By Create Date`;
              return `Sort By Latest Update Date`;
            },
            icon: (
              <Icon
                style={{ fontSize: 18, verticalAlign: 'middle' }}
                component={SortIcon}
              ></Icon>
            ),
            sortByOptions: [
              {
                value: 'createdAt',
                label: 'Create Date'
              },
              {
                value: 'updatedAt',
                label: 'Latest Update Date'
              }
            ],
            options: [
              {
                value: 'desc',
                label: 'Newest to Oldest'
              },
              {
                value: 'asc',
                label: 'Oldest to Newest'
              }
            ]
          },
          {
            key: 'status',
            type: EFilterItemType.DROPDOWN,
            icon: <RocketOutlined />,
            label: (selected) => {
              if (selected.includes('all')) {
                return 'All Status';
              }
              return `Status (${selected.length})`;
            },
            options: [
              {
                value: 'active',
                label: 'Active'
              },
              {
                value: 'inactive',
                label: 'Inactive'
              }
            ],
            haveOptionAll: true
          }
        ]}
      ></FilterBar>
      <WrapperTable>
        <Table
          tableLayout="fixed"
          loading={list.isLoading || list.isFetching}
          onChange={onTableChange}
          rowKey={(record) => record.id}
          dataSource={templates?.data || []}
          columns={templateColumns}
          scroll={{ x: 'max-content' }}
          pagination={{
            ...(templates?.pagination as any),
            current: templates?.pagination?.page,
            showSizeChanger: true,
            showTotal: (total, range) => {
              return (
                <StyledTotalLabel>
                  {range[0]}-{range[1]} of {total}
                </StyledTotalLabel>
              );
            }
          }}
        ></Table>
      </WrapperTable>
    </ListLayout>
  );
};
TemplateManagementPage.Index = Index;
TemplateManagementPage.Information = TemplateInformation;
export default TemplateManagementPage;
